import React, { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { BackLink } from "@/components/BackLink";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { VOLUNTEER } from "@/constants/testIds";
import { toast } from "sonner";

export default function LogHours() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const presetOpp = sp.get("opportunity");

  const [apps, setApps] = useState([]);
  const [opportunityId, setOpportunityId] = useState(presetOpp || "");
  const [orgUserId, setOrgUserId] = useState("");
  const [hours, setHours] = useState(1);
  const [narrative, setNarrative] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    api.myApplications().then((a) => {
      setApps(a);
      if (presetOpp) {
        const found = a.find(x => x.opportunity_id === presetOpp);
        if (found) setOrgUserId(found.org_user_id);
      }
    }).catch(() => {});
  }, [presetOpp]);

  const onOppChange = (val) => {
    setOpportunityId(val);
    const found = apps.find(a => a.opportunity_id === val);
    if (found) setOrgUserId(found.org_user_id);
  };

  const submit = async () => {
    if (!orgUserId) return toast.error("Select an opportunity or organisation");
    if (!narrative) return toast.error("Add a short narrative");
    try {
      await api.logHours({
        org_user_id: orgUserId,
        opportunity_id: opportunityId || null,
        hours: Number(hours),
        narrative,
        date: new Date(date).toISOString(),
      });
      toast.success("Hours submitted for approval");
      nav("/dashboard");
    } catch (e) {
      toast.error("Could not submit");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <Seo title="Log hours" />
      <BackLink />
      <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Submit contribution</div>
      <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">Log your volunteering hours</h1>
      <p className="text-neutral-600 mt-2 text-sm">Tell the organisation what you did. They'll approve it and it'll count towards your badges.</p>

      <Card className="mt-8 border-neutral-200">
        <CardContent className="p-8 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Opportunity</label>
            <Select value={opportunityId} onValueChange={onOppChange}>
              <SelectTrigger data-testid={VOLUNTEER.logHoursOpp} className="mt-2 h-11"><SelectValue placeholder="Select from your applications" /></SelectTrigger>
              <SelectContent>
                {apps.length === 0 && <SelectItem value="none" disabled>No applications yet</SelectItem>}
                {apps.map(a => (
                  <SelectItem key={a.id} value={a.opportunity_id}>{a.opportunity_title} — {a.org_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Hours</label>
              <Input data-testid={VOLUNTEER.logHoursAmount} type="number" min={0.25} step={0.25} value={hours} onChange={(e) => setHours(e.target.value)} className="mt-2 h-11" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Date</label>
              <Input data-testid={VOLUNTEER.logHoursDate} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-2 h-11" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">What did you do?</label>
            <Textarea data-testid={VOLUNTEER.logHoursNarrative} value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={5}
              placeholder="A short story — what you achieved, who you helped, what you learned." className="mt-2" />
          </div>
          <div className="flex justify-end">
            <Button data-testid={VOLUNTEER.logHoursSubmit} onClick={submit} className="rounded-full bg-forest hover:bg-forest-hover h-11 px-6">Submit for approval</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
